// ---------------------------------------------------------------
// Human-readable catalog for everything the Teltonika FMM00A sends.
// Each entry: friendly label, unit, group, and optional value formatter.
// ---------------------------------------------------------------

const secondsToHm = (s) => `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
const yesNo = (v) => (v ? 'Yes' : 'No');

const FUEL_TYPES = { 0: 'Not available', 1: 'Gasoline', 2: 'Methanol', 3: 'Ethanol', 4: 'Diesel', 5: 'LPG', 6: 'CNG', 7: 'Propane', 8: 'Electric', 9: 'Hybrid gasoline', 17: 'Hybrid diesel' };
const SLEEP_MODES = { 0: 'Awake', 1: 'GPS sleep', 2: 'Deep sleep', 3: 'Online deep sleep', 4: 'Ultra sleep' };
const GNSS_STATUS = { 0: 'GPS off', 1: 'GPS on — position fixed', 2: 'GPS on — searching signal', 3: 'GPS sleeping' };

export const OBD_CATALOG = {
  // ---- Engine ----
  io36: { label: 'Engine RPM', unit: 'rpm', group: 'Engine' },
  io31: { label: 'Engine load', unit: '%', group: 'Engine' },
  io52: { label: 'Absolute engine load', unit: '%', group: 'Engine' },
  io41: { label: 'Throttle pedal position', unit: '%', group: 'Engine' },
  io38: { label: 'Ignition timing advance', unit: '°', group: 'Engine' },
  io42: { label: 'Time since engine started', group: 'Engine', fmt: secondsToHm },
  io58: { label: 'Engine oil temperature', unit: '°C', group: 'Engine' },
  ignition: { label: 'Ignition (key position)', group: 'Engine', fmt: (v) => (v ? 'On' : 'Off') },

  // ---- Fuel ----
  io48: { label: 'Fuel level in tank', unit: '%', group: 'Fuel' },
  io390: { label: 'Fuel level (from dashboard computer)', group: 'Fuel' },
  io12: { label: 'Estimated fuel used (lifetime)', group: 'Fuel', fmt: (v) => `${(v / 10).toFixed(1)} L` },
  io60: { label: 'Fuel consumption rate', unit: 'L/h', group: 'Fuel' },
  io33: { label: 'Fuel trim (engine self-adjustment)', unit: '%', group: 'Fuel' },
  io34: { label: 'Fuel pressure', unit: 'kPa', group: 'Fuel' },
  io45: { label: 'Fuel rail pressure', unit: 'kPa', group: 'Fuel' },
  io759: { label: 'Fuel type', group: 'Fuel', fmt: (v) => FUEL_TYPES[v] || `Type ${v}` },

  // ---- Health & alerts ----
  io30: { label: 'Active fault codes (check engine)', group: 'Health & Alerts' },
  io281: { label: 'Fault code list (DTC)', group: 'Health & Alerts' },
  io43: { label: 'Distance driven with warning light on', unit: 'km', group: 'Health & Alerts' },
  io49: { label: 'Distance since fault codes cleared', unit: 'km', group: 'Health & Alerts' },
  io54: { label: 'Time driven with warning light on', group: 'Health & Alerts', fmt: secondsToHm },
  io55: { label: 'Time since fault codes cleared', group: 'Health & Alerts', fmt: secondsToHm },

  // ---- Temperatures & environment ----
  io32: { label: 'Engine coolant temperature', unit: '°C', group: 'Temperatures' },
  io39: { label: 'Intake air temperature', unit: '°C', group: 'Temperatures' },
  io53: { label: 'Outside air temperature', unit: '°C', group: 'Temperatures' },
  io50: { label: 'Barometric pressure', unit: 'kPa', group: 'Temperatures' },
  io35: { label: 'Intake manifold pressure', unit: 'kPa', group: 'Temperatures' },

  // ---- Electrical ----
  power: { label: 'Vehicle battery voltage', unit: 'V', group: 'Electrical' },
  battery: { label: 'Tracker internal battery', unit: 'V', group: 'Electrical' },
  io51: { label: 'ECU (computer) voltage', group: 'Electrical', fmt: (v) => `${(v / 1000).toFixed(2)} V` },
  io68: { label: 'Battery current draw', unit: 'A', group: 'Electrical' },
  io57: { label: 'Hybrid battery remaining life', unit: '%', group: 'Electrical' },

  // ---- Speed & distance ----
  io37: { label: 'Speed (from vehicle computer)', unit: 'km/h', group: 'Speed & Distance' },
  io24: { label: 'Speed (from GPS)', unit: 'km/h', group: 'Speed & Distance' },
  io389: { label: 'Vehicle odometer (from dashboard)', unit: 'km', group: 'Speed & Distance' },
  odometer: { label: 'Trip odometer (device)', group: 'Speed & Distance', fmt: (v) => `${(v / 1000).toFixed(1)} km` },
  totalDistance: { label: 'Total distance (tracked)', group: 'Speed & Distance', fmt: (v) => `${(v / 1000).toFixed(1)} km` },
  distance: { label: 'Distance since last report', group: 'Speed & Distance', fmt: (v) => `${(v / 1000).toFixed(2)} km` },
  hours: { label: 'Engine hours (tracked)', group: 'Speed & Distance', fmt: (v) => secondsToHm(v / 1000) },

  // ---- GPS quality ----
  sat: { label: 'Satellites in view', group: 'GPS Quality' },
  pdop: { label: 'GPS precision (PDOP — lower is better)', group: 'GPS Quality' },
  hdop: { label: 'GPS accuracy (HDOP — lower is better)', group: 'GPS Quality' },
  io69: { label: 'GPS status', group: 'GPS Quality', fmt: (v) => GNSS_STATUS[v] || v },
  io303: { label: 'Movement detected', group: 'GPS Quality', fmt: yesNo },
  motion: { label: 'In motion', group: 'GPS Quality', fmt: yesNo },

  // ---- Device & network ----
  rssi: { label: 'Cell signal strength (0–5)', group: 'Device & Network' },
  operator: { label: 'Cell network code', group: 'Device & Network' },
  io200: { label: 'Power-saving mode', group: 'Device & Network', fmt: (v) => SLEEP_MODES[v] || v },
  io383: { label: 'Motion sensor calibration', group: 'Device & Network', fmt: (v) => (v === 3 ? 'Calibrated' : `Status ${v}`) },

  // ---- Vehicle identity ----
  vin: { label: 'VIN (vehicle identification number)', group: 'Vehicle Identity' },
};

export const GROUP_ORDER = [
  'Engine', 'Fuel', 'Health & Alerts', 'Temperatures', 'Electrical',
  'Speed & Distance', 'GPS Quality', 'Device & Network', 'Vehicle Identity', 'Other',
];

// Keys that are internal/duplicated — not worth showing
const HIDDEN = new Set(['event', 'priority', 'io16']);

export function describeAttribute(key, value) {
  if (HIDDEN.has(key)) return null;
  const entry = OBD_CATALOG[key];
  if (entry) {
    const shown = entry.fmt ? entry.fmt(value) : `${value}${entry.unit ? ` ${entry.unit}` : ''}`;
    return { label: entry.label, value: shown, group: entry.group, raw: key };
  }
  // Unknown raw manufacturer codes (io113, io449, io542…) are internal tracker
  // values that mean nothing to a driver — hide them so the page only shows
  // real, named readings instead of code-looking noise.
  if (/^io\d+$/.test(key)) return null;
  return null;
}
